import { List } from "@refinedev/antd";
import { IResourceComponentsProps } from "@refinedev/core";
import { Button, Form, Input, message, Select, Switch, Typography } from "antd";
import React, { useEffect } from "react";
import {
  IVersionReview,
  IVersionReviewPayload,
  useGetVersionReview,
  useVersionReview,
} from "../../api/version";
import { clearFieldErrorMessageOnChange } from "../../utils/form.constants";
import "../styles/index.css";

const rowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  minHeight: 60,
  flexWrap: "wrap",
};

const labelStyle: React.CSSProperties = {
  flex: "0 0 auto",
  fontWeight: 600,
  width: 180,
};

export const Version: React.FC<IResourceComponentsProps> = () => {
  const [form] = Form.useForm<IVersionReviewPayload>();
  const { data, refetch } = useGetVersionReview();

  const { mutate: updateVersionReview, isLoading } = useVersionReview({
    onSuccess: () => {
      message.success("Update version review successfully!");
      refetch();
    },
    onError: (error: { message: string }) => {
      message.error(error?.message || "Update version review failed!");
    },
  });

  const onSubmit = (values: IVersionReviewPayload) => {
    updateVersionReview({
      version: values.version.trim(),
      alertType: values.alertType,
      isReview: values.isReview,
    });
  };

  useEffect(() => {
    if (!data) return;

    const response = data?.data?.data;
    const versionReview: IVersionReview | undefined = Array.isArray(response)
      ? response[0]
      : response;

    if (!versionReview) return;

    form.setFieldsValue({
      version: versionReview.version,
      alertType: versionReview.alertType,
      isReview:
        versionReview.isReview ?? versionReview.isReview ?? false,
    });
  }, [data, form]);

  return (
    <List title="Version Review">
      <Form<IVersionReviewPayload>
        form={form}
        layout="vertical"
        initialValues={{
          alertType: 0,
          isReview: false,
        }}
        onFinish={onSubmit}
        onChange={() => clearFieldErrorMessageOnChange(form)}
      >
        <div style={rowStyle}>
          <Typography style={labelStyle}>Version:</Typography>
          <Form.Item
            name="version"
            rules={[
              { required: true, message: "This field is required" },
              { max: 50, message: "Version must be at most 50 characters" },
              {
                validator: (_, value) =>
                  value && value.trim().length === 0
                    ? Promise.reject(new Error("This field is required"))
                    : Promise.resolve(),
              },
            ]}
            noStyle
          >
            <Input
              placeholder="Input version"
              size="large"
              maxLength={50}
              style={{ width: 260 }}
            />
          </Form.Item>
        </div>
        <div style={rowStyle}>
          <Typography style={labelStyle}>Update Alert Type:</Typography>
          <Form.Item
            name="alertType"
            rules={[{ required: true, message: "This field is required" }]}
            noStyle
          >
            <Select
              size="large"
              style={{ width: 220 }}
              options={[
                { value: 0, label: "No Update Alert" },
                { value: 1, label: "Optional Update" },
                { value: 2, label: "Force Update" },
              ]}
            />
          </Form.Item>
        </div>
        <div style={rowStyle}>
          <Typography style={labelStyle}>Review Mode:</Typography>
          <Form.Item name="isReview" valuePropName="checked" noStyle>
            <Switch size="default" checkedChildren="ON" unCheckedChildren="OFF" />
          </Form.Item>
        </div>
        <Button
          key="save"
          type="primary"
          onClick={() => form.submit()}
          loading={isLoading}
          className="smartFooterButtonXS"
        >
          Save
        </Button>
      </Form>
    </List>
  );
};
